// Pure transcript transform run at a chat run's `agent_end`.
//
// Two jobs, both scoped to ONLY the turns created during the run that
// just finished (`runStartTurns` = the number of turn items that already
// existed when this run began). Never touch earlier runs' turns: those
// hold already-resolved Save cards and settled history, and the items
// array is persisted per-chat — mutating a prior run's turn would both
// remount (and reset) a resolved card and rewrite saved history.
//
//   1. suggest_kernel is a registration hook, not an inline answer. Pull
//      this run's kernel-suggestion tool nodes out of wherever they
//      landed and re-attach them as the last child of the final turn, so
//      the Save card reads AFTER the closing summary rather than wedged
//      mid-transcript.
//   2. Drop this run's now-empty turns (the vacated suggest turn + any
//      vestigial wrap-up turn) so `final` lands on the last turn that did
//      real work — keeping the run's last turn if it produced nothing
//      else, so there's somewhere to host the card and the flag.

function lastTurnIndex(items) {
    for (let i = items.length - 1; i >= 0; i--) {
        if (items[i].kind === "turn") return i;
    }
    return -1;
}

export function isSuggestionNode(child) {
    return child.kind === "tool" && child.details?.kind === "kernelSuggestion";
}

// Children a turn should render. suggest_kernel Save cards are relocated
// to the final turn at agent_end (finalizeRunTranscript) and hidden
// everywhere else, so a non-final turn drops them — otherwise the card
// would flash inline mid-run before relocation.
export function visibleTurnChildren(node) {
    const children = node.children || [];
    return node.final ? children : children.filter((c) => !isSuggestionNode(c));
}

// A done, non-final turn with nothing visible left renders nothing: its
// only child was a card that agent_end will relocate, or it was the
// model's vestigial wrap-up turn (which agent_end also drops from the
// tree). Hiding it avoids a stray empty "Agent" block while the run
// finishes.
export function isTurnHidden(node) {
    return node.status === "done" && !node.final && visibleTurnChildren(node).length === 0;
}

// Indices into `items` of the turns that belong to the current run
// (turn ordinal >= runStartTurns).
function currentRunTurnIndices(items, runStartTurns) {
    const idxs = [];
    let ord = -1;
    items.forEach((it, i) => {
        if (it.kind !== "turn") return;
        ord += 1;
        if (ord >= runStartTurns) idxs.push(i);
    });
    return idxs;
}

export function finalizeRunTranscript(items, { runStartTurns = 0, errorMessage = null } = {}) {
    let next = items;

    // 1. Extract this run's suggestion nodes.
    const currentIdx = new Set(currentRunTurnIndices(next, runStartTurns));
    const suggestions = [];
    next = next.map((it, i) => {
        if (!currentIdx.has(i)) return it;
        const kept = it.children.filter((c) => {
            if (isSuggestionNode(c)) {
                suggestions.push(c);
                return false;
            }
            return true;
        });
        return kept.length === it.children.length ? it : { ...it, children: kept };
    });

    // 2. Drop this run's empty turns. Indices are unchanged since step 1
    //    only rewrote children, so `currentIdx` still applies. Keep the
    //    run's last turn if every current-run turn ended up empty.
    const currentTurns = [...currentIdx];
    const runHasWork = currentTurns.some((i) => next[i].children.length > 0);
    const lastCurrent = currentTurns[currentTurns.length - 1];
    next = next.filter((it, i) => {
        if (!currentIdx.has(i)) return true;
        if (it.children.length > 0) return true;
        // Empty current-run turn: drop it, unless the run produced no
        // other work and this is its last turn (needed to host the card
        // / final flag).
        return !runHasWork && i === lastCurrent;
    });

    // 3. Surface a fatal error, then the relocated card(s), as the last
    //    children of the final turn.
    const appendToLast = (child) => {
        const idx = lastTurnIndex(next);
        if (idx < 0) return;
        next = next.slice();
        next[idx] = { ...next[idx], children: [...next[idx].children, child] };
    };
    if (errorMessage) appendToLast({ kind: "assistant", text: `⚠ ${errorMessage}` });
    for (const s of suggestions) appendToLast(s);

    // 4. Flag the trailing turn as the final turn in this run. Intermediate
    //    turns keep their tool-count label; only the last turn reads
    //    "complete".
    const fidx = lastTurnIndex(next);
    if (fidx >= 0) {
        next = next.slice();
        next[fidx] = { ...next[fidx], final: true };
    }
    return next;
}
